import { Button, Box, Typography, useTheme, useMediaQuery, IconButton } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { Browser } from "@capacitor/browser";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "./homepage.module.css";
import { firstPageStories } from "../../mockup/firstPageStories";
import imagePageOne from "../../assets/imagePageOne.svg";
import { LinearProgress } from "@mui/material";
import SplashScreen from "../../components/SplashScreen/SplashScreen";
import { callbackUri } from "src/auth.config";
import { isPlatform } from "@ionic/react";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { isE2eAuthBypassEnabled } from "src/utils/e2eAuthBypass";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { markAnalyticsSignupIntent, trackSignupScreenSeenAt } from "src/utils/analyticsCapFirebase";

const GetStartedPage = () => {
  // Auth0 logic
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  //Import navigation
  const navigate = useNavigate();
  // State for the story index
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  // State for the story animation
  const [showingNewStory, setShowingNewStory] = useState(true);
  // Prevent stacked story timeouts (fast taps) from flashing the wrong image
  const storyChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // State for the splash screen
  const [showSplash, setShowSplash] = useState(!isE2eAuthBypassEnabled());

  // Lock/unlock body scroll based on the splash screen's visibility state
  useEffect(() => {
    const preventScroll = (e: TouchEvent) => {
      e.preventDefault();
    };

    if (showSplash) {
      // When splash is showing, lock scroll
      document.body.style.overflow = "hidden";
      document.body.addEventListener("touchmove", preventScroll, { passive: false });
    }

    // When `showSplash` becomes false, the cleanup function from the previous render will run, restoring scroll.
    return () => {
      document.body.style.overflow = "";
      document.body.removeEventListener("touchmove", preventScroll);
    };
  }, [showSplash]); // The dependency on `showSplash` is the key to making this work

  const iosOrAndroid = isPlatform("hybrid");
  const isMobileWeb = isMobileWebBrowser();
  const isMobile = iosOrAndroid || isMobileWeb;

  //THIS IS LOGIC FOR THE TOUCH EVENTS IN MOBILE. FOR NOW ITS DISABLED, BUT WE CAN USE IT IN THE FUTURE (FOR THAT I DONT DELETE IT)
  // New states for the swipe
  // const [touchStart, setTouchStart] = useState(null);
  // const [touchEnd, setTouchEnd] = useState(null);
  // // Minimum swipe distance
  // const minSwipeDistance = 50;

  // Theme and media query for mobile/tablet
  const theme = useTheme();
  // const isMobileOrTablet = useMediaQuery(theme.breakpoints.between("xs", "lg"));
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg")) && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
  // For tablets (between 600px and 1200px): require touch + exclude short landscape
  // so Mac/desktop and Nest Hub (1024x600) keep phone mock; real iPads keep imgTablet
  const isTabletWidth = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isShortLandscape = useMediaQuery("(max-height: 700px) and (orientation: landscape)");
  const isTablet =
    isTabletWidth &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0) &&
    !isShortLandscape;

  const logoTargetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void trackSignupScreenSeenAt("manual_click", "get_started");
  }, []);

  useEffect(() => {
    // Preload story assets so remounted slides (key=index) paint from cache — no black gap
    firstPageStories.forEach((story) => {
      const phoneImg = new window.Image();
      phoneImg.src = story.img;
      const tabletImg = new window.Image();
      tabletImg.src = story.imgTablet;
    });
    return () => {
      if (storyChangeTimeoutRef.current) clearTimeout(storyChangeTimeoutRef.current);
    };
  }, []);

  // This variable will be true only for the last story
  const isLastStory = currentStoryIndex === firstPageStories.length - 1;

  // Redirect to home if the user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/terms-and-conditions");
    }
  }, [isAuthenticated, navigate]);

  // Function to handle the login
  const login = async (event) => {
    // Stop the event propagation
    event.stopPropagation();

    // E2E Bypass: skip Auth0 and go straight to /home
    if (isE2eAuthBypassEnabled()) {
      console.warn("[E2E BYPASS] Skipping Auth0 login and redirecting to /home.");
      navigate("/home", { replace: true });
      return;
    }

    // Login with redirect
    markAnalyticsSignupIntent();
    await loginWithRedirect({
      async openUrl(url) {
        // Redirect using Capacitor's Browser plugin
        await Browser.open({
          url,
          windowName: "_self",
        });
      },
    });
  };

  //Function to handle the next story
  const handleNextStory = () => {
    if (currentStoryIndex < firstPageStories.length - 1) {
      setShowingNewStory(false);
      if (storyChangeTimeoutRef.current) clearTimeout(storyChangeTimeoutRef.current);
      storyChangeTimeoutRef.current = setTimeout(() => {
        setCurrentStoryIndex((prev) => prev + 1);
        setShowingNewStory(true);
      }, 50);
    }
  };

  //Function to handle the previous story
  const handlePreviousStory = () => {
    if (currentStoryIndex > 0) {
      setShowingNewStory(false);
      if (storyChangeTimeoutRef.current) clearTimeout(storyChangeTimeoutRef.current);
      storyChangeTimeoutRef.current = setTimeout(() => {
        setCurrentStoryIndex((prev) => prev - 1);
        setShowingNewStory(true);
      }, 50);
    }
  };

  //Function to handle the click on the container
  const handleClick = (event) => {
    // If the click is on the mobile/tablet or the play/pause button or the login button, return
    // if (isMobileOrTablet || event.target.closest("#playPauseFab") || event.target.closest("button")) {
    //   return;
    // }
    // Get the clientX and windowWidth
    const { clientX } = event;
    const windowWidth = window.innerWidth;
    const margin = windowWidth * 0.5;

    // If the click is on the left side, go to the previous story
    if (clientX < margin) {
      handlePreviousStory();
    } else if (clientX > windowWidth - margin) {
      // If the click is on the right side, go to the next story
      handleNextStory();
    }
  };

  // Progress bar logic
  const getSubProgressWidth = (index) => (index < currentStoryIndex || index === currentStoryIndex ? 100 : 0);

  //THIS IS LOGIC FOR THE TOUCH EVENTS IN MOBILE. FOR NOW ITS DISABLED, BUT WE CAN USE IT IN THE FUTURE (FOR THAT I DONT DELETE IT)

  // Events for mobile/tablet touch
  // const onTouchStart = (e) => {
  //   if (!isMobileOrTablet || e.target.closest("button")) {
  //     return;
  //   }
  //   // Reset the touch end and start
  //   setTouchEnd(null);
  //   // Set the touch start
  //   setTouchStart(e.targetTouches[0].clientX);
  // };

  // // Set the touch end
  // const onTouchMove = (e) => {
  //   if (!isMobileOrTablet) return;
  //   setTouchEnd(e.targetTouches[0].clientX);
  // };

  // // Check if the swipe is left or right
  // const onTouchEnd = () => {
  //   // If the touch start or end is null, return
  //   if (!isMobileOrTablet || !touchStart || !touchEnd) return;
  //   // Calculate the distance between the touch start and end
  //   const distance = touchStart - touchEnd;
  //   const isLeftSwipe = distance > minSwipeDistance; // Swipe from right to left
  //   const isRightSwipe = distance < -minSwipeDistance; // Swipe from left to right

  //   if (isLeftSwipe) {
  //     handleNextStory(); // Next story
  //   } else if (isRightSwipe) {
  //     handlePreviousStory(); // Previous story
  //   }
  // };

  // useEffect(() => {
  //   // Save original overflow values
  //   const originalHtmlOverflow = document.documentElement.style.overflowY;
  //   const originalBodyOverflow = document.body.style.overflowY;

  //   // Enable scroll for this page
  //   document.documentElement.style.overflowY = "auto";
  //   document.body.style.overflowY = "auto";

  //   // Restore original values on unmount
  //   return () => {
  //     document.documentElement.style.overflowY = originalHtmlOverflow;
  //     document.body.style.overflowY = originalBodyOverflow;
  //   };
  // }, []);

  return (
    <>
      {/* if the user is not authenticated, show the splash screen */}
      {!isAuthenticated && showSplash && <SplashScreen onVideoEnd={() => setShowSplash(false)} logoTargetRef={logoTargetRef} />}

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          opacity: showSplash ? 0 : 1,
          transition: "opacity 0.5s ease-in-out",
          pointerEvents: showSplash ? "none" : "auto",
          // paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            position: "relative",
            mt: 0,
            pt: {
              xs: iosOrAndroid ? 8 : 6,
              sm: iosOrAndroid ? 6 : 1,
              md: 1,
              lg: 0,
              "@media (max-height: 667px)": iosOrAndroid ? 2 : 0,
            },
            justifyContent: {
              xs: "flex-start",
              "@media (max-height: 667px)": {
                justifyContent: "flex-start",
              },
              sm: "flex-start",
              lg: "center",
            },
            //all the next properties are to prevent moving the page when the user is swiping the stories
            touchAction: isMobileWeb || !iosOrAndroid ? "auto" : "none",
            // touchAction: "pan-y",
            userSelect: isMobileWeb || !iosOrAndroid ? "auto" : "none",
            WebkitTouchCallout: isMobileWeb || !iosOrAndroid ? "auto" : "none",
            "& *": {
              touchAction: isMobileWeb || !iosOrAndroid ? "auto" : "none",
              // touchAction: "pan-y",
              userSelect: isMobileWeb || !iosOrAndroid ? "auto" : "none",
              pointerEvents: "auto",
            },
          }}
          alignItems="center"
          bgcolor="#000000"
          height="100vh"
          // height="100%"
          // minHeight="100svh"
          overflow="hidden"
          onClick={handleClick}
          onTouchEnd={handleClick}
          // Events for mobile/tablet touch
          // onTouchStart={isMobileOrTablet ? onTouchStart : null}
          // onTouchMove={isMobileOrTablet ? onTouchMove : null}
          // onTouchEnd={isMobileOrTablet ? onTouchEnd : null}
          // Class name for the container for the width of the app
          className={styles.boxContainerApp}
        >
          {/* Progress bars */}
          <Box
            display="flex"
            width="100%"
            p={1}
            sx={{
              position: "absolute",
              top: 0,
              pt: {
                xs: iosOrAndroid ? 4 : 3, // more padding in mobile/tablet
                sm: iosOrAndroid ? 2 : 3,
                md: 3,
                lg: 3,
                "@media (max-height: 667px)": iosOrAndroid ? 0 : 3,
              },
            }}
          >
            {firstPageStories.map((story, index) => (
              <Box key={index} flex={1} mx={0.1}>
                <LinearProgress
                  variant="determinate"
                  color="inherit"
                  value={getSubProgressWidth(index)}
                  sx={{
                    height: "2px",
                    color: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    bgcolor: `${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Story content */}
          <Box
            sx={{
              width: "100%",
              pt: {
                xs: 0,
                "@media (min-height: 667px)": {
                  // iPhone SE height
                  pt: 1,
                },
                sm: 6,
                lg: 1,
              },
              // flex: {
              //   xs: 0.8,
              //   // "@media (max-height: 667px)": {
              //   //   flex: "auto",
              //   // },
              // },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: {
                xs: 1,
                "@media (max-height: 667px)": {
                  gap: 2,
                },
              },
            }}
          >
            <Typography
              variant="h4"
              color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}
              textAlign="center"
              className={`${styles.tradeTeam} ${showingNewStory ? styles.storyFadeIn : ""}`}
              onAnimationEnd={() => setShowingNewStory(false)}
              sx={{
                fontSize: "18px",
                "@media (max-height: 667px)": {
                  fontSize: "16px",
                },
              }}
            >
              {firstPageStories[currentStoryIndex].title}
            </Typography>

            <Typography
              variant="body1"
              color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}
              textAlign="center"
              className={showingNewStory ? styles.storyFadeIn : ""}
              sx={{
                width: { xs: "85%", lg: "70%" },
                height: "60px",
                fontSize: "16px",
                "@media (max-height: 667px)": {
                  fontSize: "14px",
                },
              }}
            >
              {firstPageStories[currentStoryIndex].content}
            </Typography>

            {/* Story image + side nav arrows (fixed to container edges; hide at ends) */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                alignSelf: "stretch",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: {
                  xs: 2,
                  "@media (max-height: 667px)": {
                    mt: 3,
                  },
                  lg: 0,
                },
              }}
            >
              {currentStoryIndex > 0 && (
                <IconButton
                  aria-label="previous story"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviousStory();
                  }}
                  sx={{
                    position: "absolute",
                    left: isMobile ? "5px" : "100px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    p: 0.25,
                  }}
                >
                  <ArrowLeftIcon sx={{ fontSize: 120 }} />
                </IconButton>
              )}
              <Box
                component="img"
                key={currentStoryIndex}
                src={isTablet ? firstPageStories[currentStoryIndex].imgTablet : firstPageStories[currentStoryIndex].img} // If the screen is a tablet, use the tablet image, otherwise use the mobile image
                alt={firstPageStories[currentStoryIndex].name}
                className={showingNewStory ? styles.storyFadeIn : ""}
                sx={{
                  // Conditional width for tablet
                  width: {
                    xs: isLastStory ? "450px" : "400px",
                    sm: isLastStory ? "600px" : "500px", // 600px for the last story on sm, 500px for others
                    md: isLastStory ? "600px" : "500px", // 600px for the last story on md, 500px for others
                    lg: "auto",
                  },
                  // if is mobile, height 375 and if is bigger 480px please
                  //   height:  isMobile ? "500px" : "375px", 
                  height: "300px",
                  objectFit: "contain",
                  // Only shrink on short *narrow* phones — not wide short screens (Nest Hub 1024x600)
                  "@media (max-height: 667px) and (max-width: 600px)": {
                    width: "400px",
                    height: "300px",
                  },
                }}
              />
              {!isLastStory && (
                <IconButton
                  aria-label="next story"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextStory();
                  }}
                  sx={{
                    position: "absolute",
                    right: isMobile ? "5px" : "100px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    p: 0.25,
                  }}
                >
                  <ArrowRightIcon sx={{ fontSize: 120 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Box final for the logo (reserved space for the logo after the splash screen) and the button */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
            sx={{
              position: "relative",
              width: "100%",
              bottom: 0,
              left: 0,
              right: 0,
            }}
          >
            {/* Reserved space for the logo */}
            <Box
              ref={logoTargetRef}
              sx={{
                // width: { xs: "180px", sm: "200px", md: "220px" },
                // height: { xs: "50px", sm: "60px", md: "70px" },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                // mb: 1, // Bottom margin before the button
                mb: 3, // Bottom margin before the button
              }}
            />

            <Box display="flex" flexDirection={isMobileOrTablet ? "column" : "row"} alignItems="center" gap={1} p={0}>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  login(e);
                }}
                sx={{
                  fontFamily: "ulp-font, -apple-system, BlinkMacSystemFont, Roboto, Helvetica, sans-serif",
                  fontSize: "16px",
                  fontWeight: "normal",
                  borderRadius: 0.3,
                  width: "250px",
                  height: "40px",
                  bgcolor: "#000000",
                  border: "1px solid #ffffff",
                  "&:hover": {
                    bgcolor: "#131313",
                  },
                }}
              >
                Log In
                {/* Sign Up For Free */}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  login(e);
                }}
                sx={{
                  fontFamily: "ulp-font, -apple-system, BlinkMacSystemFont, Roboto, Helvetica, sans-serif",
                  fontSize: "16px",
                  fontWeight: "normal",
                  borderRadius: 0.3,
                  width: "250px",
                  height: "40px",
                  bgcolor: "#000000",
                  border: "1px solid #ffffff",
                  "&:hover": {
                    bgcolor: "#131313",
                  },
                }}
              >
                {/* Log In */}
                Sign Up For Free
              </Button>
            </Box>
            {/* Button to access guest home / (former /home-free, non-logged experience) */}
            {/* <Box >
              <Button
                variant="text"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/", { replace: true });
                }}
                sx={{
                  fontFamily: "ulp-font, -apple-system, BlinkMacSystemFont, Roboto, Helvetica, sans-serif",
                  fontSize: "14px",
                  fontWeight: "normal",
                  color: "#ffffff",
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Try without login
              </Button>
            </Box> */}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default GetStartedPage;
